import { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Plus, Trash2, GripVertical, ExternalLink, UserCircle, Layout, Share2, Palette, MessageSquare, CheckSquare } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BioPageData, defaultBioPageData, bioPageToContentData, contentDataToBioPage, BioPageLink, BioPageLeadForm } from '@/lib/bio-page-utils';

interface BioPageFormProps {
  contentData: Record<string, string>;
  onUpdate: (data: Record<string, string>) => void;
}

export function BioPageForm({ contentData, onUpdate }: BioPageFormProps) {
  const [data, setData] = useState<BioPageData>(() => contentDataToBioPage(contentData));

  useEffect(() => {
    onUpdate(bioPageToContentData(data));
  }, [data, onUpdate]);

  const addLink = () => {
    const newLink: BioPageLink = {
      id: Math.random().toString(36).substr(2, 9),
      label: 'New Link',
      url: 'https://',
      active: true,
    };
    setData(prev => ({ ...prev, links: [...prev.links, newLink] }));
  };

  const removeLink = (id: string) => {
    setData(prev => ({ ...prev, links: prev.links.filter(l => l.id !== id) }));
  };

  const updateLink = (id: string, updates: Partial<BioPageLink>) => {
    setData(prev => ({
      ...prev,
      links: prev.links.map(l => l.id === id ? { ...l, ...updates } : l)
    }));
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <div className="flex items-center gap-2">
        <Layout className="h-5 w-5 text-primary" />
        <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
          Bio Page (Linktree-style)
        </h3>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="space-y-6">
          {/* Profile Section */}
          <Card className="p-4 space-y-4">
            <div className="flex items-center gap-2 font-medium">
              <UserCircle className="h-4 w-4" />
              Profile
            </div>
            
            <div className="space-y-2">
              <Label>Custom URL Slug</Label>
              <div className="flex gap-2">
                <div className="flex items-center px-3 bg-muted rounded-md text-xs text-muted-foreground border">
                  nexqr.online/bio/
                </div>
                <Input 
                  placeholder="yourname" 
                  value={data.slug || ''} 
                  onChange={e => setData(prev => ({ ...prev, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') }))}
                />
              </div>
              <p className="text-[10px] text-muted-foreground">This is the unique link for your bio page.</p>
            </div>

            <div className="space-y-2">
              <Label>Profile Name</Label>
              <Input 
                placeholder="Your Name or Brand" 
                value={data.profileName} 
                onChange={e => setData(prev => ({ ...prev, profileName: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Bio</Label>
              <Textarea 
                placeholder="A short description about you..." 
                value={data.bio} 
                onChange={e => setData(prev => ({ ...prev, bio: e.target.value }))}
                className="resize-none"
              />
            </div>
          </Card>

          {/* Links Section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 font-medium">
                <ExternalLink className="h-4 w-4" />
                Links
              </div>
              <Button size="sm" variant="outline" onClick={addLink}>
                <Plus className="h-4 w-4 mr-1" /> Add Link
              </Button>
            </div>

            <div className="space-y-3">
              {data.links.map((link) => (
                <Card key={link.id} className="p-3 bg-muted/30">
                  <div className="flex gap-3">
                    <div className="flex flex-col items-center justify-center text-muted-foreground cursor-grab active:cursor-grabbing">
                      <GripVertical className="h-4 w-4" />
                    </div>
                    <div className="flex-1 space-y-2">
                      <Input 
                        placeholder="Link Label" 
                        value={link.label} 
                        onChange={e => updateLink(link.id, { label: e.target.value })}
                        className="bg-background h-8 text-xs"
                      />
                      <Input 
                        placeholder="https://..." 
                        value={link.url} 
                        onChange={e => updateLink(link.id, { url: e.target.value })}
                        className="bg-background h-8 text-xs"
                      />
                    </div>
                    <Button 
                      size="icon" 
                      variant="ghost" 
                      className="text-destructive hover:bg-destructive/10"
                      onClick={() => removeLink(link.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          </div>

          {/* Lead Capture Section */}
          <Card className="p-4 space-y-4 border-primary/20 bg-primary/5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 font-medium text-primary">
                <MessageSquare className="h-4 w-4" />
                Lead Capture Form
              </div>
              <Switch 
                checked={data.leadForm?.enabled || false} 
                onCheckedChange={checked => setData(prev => ({ 
                  ...prev, 
                  leadForm: { ...(prev.leadForm || defaultBioPageData.leadForm!), enabled: checked } 
                }))} 
              />
            </div>
            
            {(data.leadForm?.enabled) && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="space-y-4 pt-2"
              >
                <div className="space-y-2">
                  <Label className="text-xs">Form Title</Label>
                  <Input 
                    value={data.leadForm.title} 
                    onChange={e => setData(prev => ({ 
                      ...prev, 
                      leadForm: { ...prev.leadForm!, title: e.target.value } 
                    }))}
                    className="h-8 text-xs"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-xs">Description</Label>
                  <Input 
                    value={data.leadForm.description} 
                    onChange={e => setData(prev => ({ 
                      ...prev, 
                      leadForm: { ...prev.leadForm!, description: e.target.value } 
                    }))}
                    className="h-8 text-xs"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-xs">Form Fields</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="field-name" 
                        checked={data.leadForm.fields.name} 
                        onCheckedChange={checked => setData(prev => ({ 
                          ...prev, 
                          leadForm: { ...prev.leadForm!, fields: { ...prev.leadForm!.fields, name: !!checked } } 
                        }))}
                      />
                      <label htmlFor="field-name" className="text-xs">Name</label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="field-email" 
                        checked={data.leadForm.fields.email} 
                        onCheckedChange={checked => setData(prev => ({ 
                          ...prev, 
                          leadForm: { ...prev.leadForm!, fields: { ...prev.leadForm!.fields, email: !!checked } } 
                        }))}
                      />
                      <label htmlFor="field-email" className="text-xs">Email</label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="field-phone" 
                        checked={data.leadForm.fields.phone} 
                        onCheckedChange={checked => setData(prev => ({ 
                          ...prev, 
                          leadForm: { ...prev.leadForm!, fields: { ...prev.leadForm!.fields, phone: !!checked } } 
                        }))}
                      />
                      <label htmlFor="field-phone" className="text-xs">Phone</label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="field-message" 
                        checked={data.leadForm.fields.message} 
                        onCheckedChange={checked => setData(prev => ({ 
                          ...prev, 
                          leadForm: { ...prev.leadForm!, fields: { ...prev.leadForm!.fields, message: !!checked } } 
                        }))}
                      />
                      <label htmlFor="field-message" className="text-xs">Message</label>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-2">
                    <Label className="text-xs">Button Text</Label>
                    <Input 
                      value={data.leadForm.buttonText} 
                      onChange={e => setData(prev => ({ 
                        ...prev, 
                        leadForm: { ...prev.leadForm!, buttonText: e.target.value } 
                      }))}
                      className="h-8 text-xs"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Success Msg</Label>
                    <Input 
                      value={data.leadForm.successMessage} 
                      onChange={e => setData(prev => ({ 
                        ...prev, 
                        leadForm: { ...prev.leadForm!, successMessage: e.target.value } 
                      }))}
                      className="h-8 text-xs"
                    />
                  </div>
                </div>
              </motion.div>
            )}
          </Card>

          {/* Style Section */}
          <Card className="p-4 space-y-4">
            <div className="flex items-center gap-2 font-medium">
              <Palette className="h-4 w-4" />
              Appearance
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Theme</Label>
                <Select value={data.theme} onValueChange={(v: any) => setData(prev => ({ ...prev, theme: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="modern">Modern</SelectItem>
                    <SelectItem value="glass">Glass</SelectItem>
                    <SelectItem value="neon">Neon</SelectItem>
                    <SelectItem value="minimal">Minimal</SelectItem>
                    <SelectItem value="vintage">Vintage</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Accent Color</Label>
                <div className="flex gap-2">
                  <Input 
                    type="color" 
                    value={data.primaryColor} 
                    onChange={e => setData(prev => ({ ...prev, primaryColor: e.target.value }))}
                    className="w-12 p-1 h-10"
                  />
                  <Input 
                    value={data.primaryColor} 
                    onChange={e => setData(prev => ({ ...prev, primaryColor: e.target.value }))}
                    className="font-mono"
                  />
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* Live Preview */}
        <div className="hidden lg:block lg:sticky lg:top-4">
          <div className="text-sm font-medium text-muted-foreground mb-4 flex items-center gap-2">
            <Share2 className="h-4 w-4" />
            Live Preview
          </div>
          <div className="aspect-[9/16] w-full max-w-[320px] mx-auto border-8 border-slate-800 rounded-[3rem] overflow-hidden shadow-2xl bg-slate-900 relative">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-6 bg-slate-800 rounded-b-xl z-10" />
            
            {/* The actual bio page content inside the phone */}
            <div className={`h-full w-full p-6 overflow-y-auto scrollbar-hide flex flex-col items-center text-center pt-12 ${
              data.theme === 'glass' ? 'bg-gradient-to-br from-indigo-500 to-purple-600' :
              data.theme === 'neon' ? 'bg-black' :
              data.theme === 'minimal' ? 'bg-white text-black' :
              data.theme === 'vintage' ? 'bg-[#f4f1ea] text-[#2c1810]' :
              'bg-slate-50'
            }`}>
              {/* Profile Image */}
              <div className="w-20 h-20 rounded-full bg-slate-200 border-4 border-white shadow-md mb-4 flex items-center justify-center overflow-hidden">
                <UserCircle className="w-full h-full text-slate-400" />
              </div>

              <h2 className={`font-bold text-lg mb-1 ${data.theme === 'neon' ? 'text-white' : ''}`}>
                {data.profileName || 'Your Name'}
              </h2>
              <p className={`text-sm opacity-80 mb-6 px-4 ${data.theme === 'neon' ? 'text-white/80' : ''}`}>
                {data.bio || 'Bio text will appear here...'}
              </p>

              <div className="w-full space-y-3">
                {data.links.filter(l => l.active).map(link => (
                  <div
                    key={link.id}
                    className={`w-full py-3 px-4 rounded-xl text-sm font-medium transition-all shadow-sm ${
                      data.theme === 'glass' ? 'bg-white/20 backdrop-blur-md text-white border border-white/30' :
                      data.theme === 'neon' ? 'bg-black border border-primary shadow-[0_0_10px_rgba(124,58,237,0.5)] text-primary' :
                      data.theme === 'minimal' ? 'bg-black text-white' :
                      data.theme === 'vintage' ? 'bg-[#2c1810] text-[#f4f1ea]' :
                      'bg-white border text-slate-900'
                    }`}
                    style={{ borderColor: data.theme === 'neon' ? data.primaryColor : undefined, color: data.theme === 'neon' ? data.primaryColor : undefined }}
                  >
                    {link.label}
                  </div>
                ))}
              </div>

              {data.leadForm?.enabled && (
                <div className={`w-full mt-6 p-4 rounded-2xl text-left ${
                  data.theme === 'glass' ? 'bg-white/10 border border-white/20 text-white' :
                  data.theme === 'neon' ? 'bg-black border border-primary/30 text-white' :
                  data.theme === 'minimal' ? 'bg-slate-50 border text-black' :
                  data.theme === 'vintage' ? 'bg-white/50 border border-[#2c1810]/10 text-[#2c1810]' :
                  'bg-white border shadow-sm text-slate-900'
                }`}>
                  <h4 className="font-bold text-sm mb-1">{data.leadForm.title}</h4>
                  <p className="text-[10px] opacity-70 mb-3">{data.leadForm.description}</p>
                  <div className="space-y-2">
                    {data.leadForm.fields.name && <div className="h-7 bg-muted/50 rounded-md border" />}
                    {data.leadForm.fields.email && <div className="h-7 bg-muted/50 rounded-md border" />}
                    <div className="h-8 bg-primary rounded-md flex items-center justify-center text-[10px] text-white font-bold" style={{ backgroundColor: data.theme !== 'minimal' && data.theme !== 'vintage' ? data.primaryColor : undefined }}>
                      {data.leadForm.buttonText}
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-auto pt-8">
                <img src="/logo.png" alt="NexQR" className="h-4 opacity-50 grayscale" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
